/**
 * Cookieless visitor statistics with the bennows Umami instance (style guide
 * 2.99, "Measurement"). The website id is public by design; the script only
 * counts on the production host (`data-domains`), never on localhost, in tests
 * or in previews. Event names are short, English and stable so reports keep
 * adding up across releases; new events go through `trackEvent`, never through
 * a script of their own.
 */
export const UMAMI_WEBSITE_ID = 'e2526c53-b86b-4ad0-8229-aeea84f00114';
export const UMAMI_SCRIPT = 'https://stats.bennows.at/script.js';
export const UMAMI_DOMAIN = 'social-studio.bennows.at';

/** Every event the app sends; the data each one carries is listed in style guide 2.99. */
export type AnalyticsEvent =
  | 'cta'
  | 'plan-select'
  | 'explainer-play'
  | 'explainer-pause'
  | 'explainer-scene'
  | 'signup-method'
  | 'signup-step'
  | 'signup-complete'
  | 'signin-method';

export type AnalyticsData = Readonly<Record<string, string | number | boolean>>;

interface UmamiPayload {
  url?: string;
  referrer?: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    umami?: { track: (event: string, data?: AnalyticsData) => void };
    socialStudioBeforeSend?: (type: string, payload: UmamiPayload) => UmamiPayload;
  }
}

const TOKEN_PATHS = /^(\/sign-up\/confirm|\/password-reset|\/invitations)\/[^/?#]+/;
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Removes one-time tokens and record ids from a path before it leaves the
 * browser: tokens must never reach a log (AGENTS.md), and ids would split
 * one page into thousands of rows in the reports.
 */
export function anonymizePath(path: string): string {
  const [pathname = ''] = path.split(/[?#]/);
  return pathname.replace(TOKEN_PATHS, '$1/:token').replace(UUID, ':id');
}

/** Adds the Umami script once, on the production host only. */
export function initAnalytics(): void {
  if (typeof document === 'undefined' || window.location.hostname !== UMAMI_DOMAIN) return;
  if (document.querySelector(`script[data-website-id="${UMAMI_WEBSITE_ID}"]`)) return;
  window.socialStudioBeforeSend = (_type, payload) => ({
    ...payload,
    url: payload.url ? anonymizePath(payload.url) : payload.url,
    referrer: payload.referrer?.includes(UMAMI_DOMAIN) ? anonymizePath(new URL(payload.referrer).pathname) : payload.referrer,
  });
  const script = document.createElement('script');
  script.defer = true;
  script.src = UMAMI_SCRIPT;
  script.dataset.websiteId = UMAMI_WEBSITE_ID;
  script.dataset.domains = UMAMI_DOMAIN;
  script.dataset.excludeSearch = 'true';
  script.dataset.excludeHash = 'true';
  script.dataset.doNotTrack = 'true';
  script.dataset.beforeSend = 'socialStudioBeforeSend';
  document.head.appendChild(script);
}

/** Sends one event; silently does nothing while the script is absent or blocked. */
export function trackEvent(event: AnalyticsEvent, data?: AnalyticsData): void {
  try {
    window.umami?.track(event, data);
  } catch {
    // Statistics never break the page.
  }
}
